import { createStore } from 'vuex';
import axios from 'axios';
import { format, addMonths } from 'date-fns';
import type { User, CalendarSummary } from '../src/shared/types';
import { analytics } from '../src/lib/analytics';
import { getSafeAvatarUrl } from '../src/shared/lib/avatar';
import { setAuthFlag, clearAuthFlag } from '../src/services/authFlag';
import { clearAllSessionData } from '../src/shared/lib/clearSessionData';
import formActionBar from './formActionBar';

axios.defaults.withCredentials = true;

const isUnauthorizedError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 401;

// ── REP7: сводка календаря для док-бейджа ────────────────────────────────────
//
// Чистая функция (экспорт для юнит-тестов): payload /calendars/schedule/ →
// {upcoming, pendingHolds, overdue}. Раньше счёт жил инлайном в
// fetchCalendarSummary и учитывал ТОЛЬКО мероприятия (mentor_events +
// registered_events) + холды: КОНСУЛЬТАЦИИ (booked_by_user/booked_for_mentor)
// не считались вовсе. Симптом «зелёная точка идеальна, число нестабильно»:
// realtime-сигнал шины дёргал ре-фетч (точка поднималась безусловно), но
// после брони/отмены ЛИЧНОЙ СЕССИИ пересчитанное число не менялось.
type ScheduleEvent = { date?: string; is_canceled?: boolean };
type ScheduleHold = { is_overdue?: boolean; status?: string };
type ScheduleBooking = {
  status?: string;
  slot?: { start_time?: string } | null;
  conference_status?: string | null;
};

// Терминальные коммерческие статусы брони — не «предстоящее».
const TERMINAL_BOOKING_STATUSES = new Set(['cancelled', 'expired', 'refunded']);
// Терминальные статусы конференции (зеркало EventCard/#269): встреча, убитая
// системой/отменой, не должна надувать счётчик «предстоящих».
const TERMINAL_CONFERENCE_STATUSES = new Set([
  'late_canceled',
  'no_show_mentor',
  'no_show_mentee',
  'canceled_by_mentee',
  'canceled_by_mentor',
  'refunded',
]);

export function computeCalendarSummary(data: Record<string, unknown>): CalendarSummary {
  const mentorEvents: ScheduleEvent[] = Array.isArray(data.mentor_events) ? data.mentor_events : [];
  const registeredEvents: ScheduleEvent[] = Array.isArray(data.registered_events) ? data.registered_events : [];
  const rawPendingHolds: ScheduleHold[] = Array.isArray(data.pending_holds) ? data.pending_holds : [];
  const bookedByUser: ScheduleBooking[] = Array.isArray(data.booked_by_user) ? data.booked_by_user : [];
  const bookedForMentor: ScheduleBooking[] = Array.isArray(data.booked_for_mentor) ? data.booked_for_mentor : [];

  const isUpcoming = (dateValue?: string) => {
    if (!dateValue) return false;
    const eventDate = new Date(dateValue).getTime();
    return Number.isFinite(eventDate) && eventDate >= Date.now();
  };

  const upcomingLeading = mentorEvents.filter((event) => !event.is_canceled && isUpcoming(event.date)).length;
  const upcomingParticipating = registeredEvents.filter((event) => !event.is_canceled && isUpcoming(event.date)).length;

  const isUpcomingBooking = (booking: ScheduleBooking) =>
    !TERMINAL_BOOKING_STATUSES.has(String(booking.status ?? '')) &&
    !TERMINAL_CONFERENCE_STATUSES.has(String(booking.conference_status ?? '')) &&
    isUpcoming(booking.slot?.start_time);
  const upcomingConsultations =
    bookedByUser.filter(isUpcomingBooking).length +
    bookedForMentor.filter(isUpcomingBooking).length;

  // Приводим холды к тем же правилам, что и CalendarPage.vue
  const normalizedHolds = rawPendingHolds.map((hold) => {
    const statusKey = typeof hold.status === 'string' ? hold.status : 'active';
    const statusMap: Record<string, 'pending' | 'cancelled' | 'done'> = {
      active: 'pending',
      expired: 'cancelled',
      cancelled: 'cancelled',
      confirmed: 'done',
    };
    const normalizedStatus = statusMap[statusKey] ?? 'pending';
    const isOverdue = Boolean(hold?.is_overdue);
    return { normalizedStatus, isOverdue };
  });

  const pendingHoldCount = normalizedHolds.filter((hold) => hold.normalizedStatus === 'pending').length;
  const overdueHoldCount = normalizedHolds.filter((hold) => hold.isOverdue).length;

  return {
    upcoming: upcomingLeading + upcomingParticipating + upcomingConsultations,
    pendingHolds: pendingHoldCount,
    overdue: overdueHoldCount,
  };
}

/**
 * Issue #10 (P1, partial): backend-vocabulary intent declaration that
 * survived an OAuth round-trip via state-param. SocialCallback commits it
 * before redirecting; the future OnboardingShell (#5) reads it to decide
 * landing page. `null` = no declared intent (anonymous user or non-social
 * flow). Cleared on logout/clearUser so a second user on the same browser
 * does not inherit the previous user's choice (same hygiene rule as #3
 * leak audit).
 */
export type PendingIntent = 'mentor' | 'menti';

interface State {
  user: User | null;
  userChecked: boolean;
  calendarSummary: CalendarSummary;
  /** «В календарь добавилось новое» — точка на вкладке дока до открытия календаря. */
  calendarHasNew: boolean;
  pendingIntent: PendingIntent | null;
}

export default createStore({
  state: {
    user: null,
    userChecked: false,
    calendarSummary: {
      upcoming: 0,
      pendingHolds: 0,
      overdue: 0,
    },
    calendarHasNew: false,
    pendingIntent: null,
  } as State,

  mutations: {
    setUser(state: State, user: User) {
      state.user = { ...user, avatar_url: getSafeAvatarUrl(user.avatar_url) };
      state.userChecked = true;
      setAuthFlag();
    },
    clearUser(state: State) {
      state.user = null;
      state.userChecked = true;
      state.calendarSummary = { upcoming: 0, pendingHolds: 0, overdue: 0 };
      state.calendarHasNew = false;
      state.pendingIntent = null;
      clearAuthFlag();
      // Issue #3 — единственная точка очистки session-scoped storage
      // (drafts / wizard / flow-flags). Вызывается отсюда, потому что
      // clearUser срабатывает на всех путях потери сессии: explicit
      // logout, 401-fail в fetchUser, прямой commit из UI.
      clearAllSessionData();
    },
    setUserChecked(state: State, checked: boolean) {
      state.userChecked = checked;
    },
    setCalendarSummary(state: State, summary: CalendarSummary) {
      state.calendarSummary = summary;
    },
    setCalendarHasNew(state: State, value: boolean) {
      state.calendarHasNew = value;
    },
    setPendingIntent(state: State, intent: PendingIntent | null) {
      state.pendingIntent = intent;
    },
  },

  actions: {
    async fetchUser({ commit, dispatch }) {
      try {
        // suppressErrorToast — boot-time probe: для анонимного user'а 401/404 —
        // нормальное состояние, не error.  Реальные 5xx упадут в catch и
        // залогируются ниже.
        const response = await axios.get('/users/me/', {
          params: { full: 1 },
          suppressErrorToast: true,
        } as any);
        console.log('fetch user response:', response.data);
        commit('setUser', response.data);
        await dispatch('fetchCalendarSummary');
      } catch (error) {
        if (!isUnauthorizedError(error)) {
          console.error('Ошибка при получении пользователя:', error);
        }
        commit('clearUser');
        throw error;
      }
    },

    async login({ dispatch }, { email, passcode }) {
      try {
        // suppressErrorToast — LoginPage renders its own contextual error
        // copy ("Неверный код или ошибка сервера") in its catch branch.
        await axios.post(
          '/verify-passcode/verify_login/',
          { email, passcode },
          { suppressErrorToast: true } as any,
        );
        await dispatch('fetchUser');
      } catch (error) {
        console.error('Ошибка при входе:', error);
        throw error;
      }
    },

    async register(_, { email, role }) {
      try {
        // suppressErrorToast — RegisterPage renders an inline error in the
        // email step via resolveRegistrationError + showToast.
        await axios.post(
          '/registration/register/',
          { email, role },
          { suppressErrorToast: true } as any,
        );
        analytics.trackLeadSubmit({ lead_type: 'registration', role });
      } catch (error) {
        console.error('Ошибка при регистрации:', error);
        throw error;
      }
    },

    async confirmRegistration({ dispatch }, { email, passcode, lid, source }) {
      try {
        // suppressErrorToast — RegisterPage's OTP step surfaces attempts-left
        // and a tailored message via resolveOtpError; the global toast would
        // duplicate that.
        await axios.post(
          '/registration/confirm_registration/',
          { email, passcode, lid, source },
          { suppressErrorToast: true } as any,
        );
        analytics.trackLeadSubmit({ lead_type: 'registration_confirmed' });
        await dispatch('fetchUser');
      } catch (error) {
        console.error('Ошибка при подтверждении регистрации:', error);
        throw error;
      }
    },

    async logout({ commit }) {
      try {
        // suppressErrorToast — выход не должен выплёскивать сырой `detail`
        // бэкенда в тост (на /login всплывало английское «Invalid token»).
        // Локальное состояние чистится в любом случае, а страница выхода сама
        // решает, что показывать пользователю.
        await axios.post('/logout/', {}, { suppressErrorToast: true } as any);
        commit('clearUser');
      } catch (error) {
        console.error('Ошибка при выходе:', error);
        commit('clearUser');
      }
    },

    async updatePhone({ commit, state }, { phone }: { phone: string }) {
      const { data } = await axios.post('/accounts/me/phone/', { phone });
      if (state.user) {
        commit('setUser', { ...state.user, phone: data.phone });
      }
      return data;
    },
    async fetchCalendarSummary({ commit, getters }) {
      if (!getters.isAuthenticated) {
        commit('setCalendarSummary', { upcoming: 0, pendingHolds: 0, overdue: 0 });
        return;
      }

      try {
        const startDate = format(new Date(), 'yyyy-MM-dd');
        const endDate = format(addMonths(new Date(), 1), 'yyyy-MM-dd');
        const { data } = await axios.get('/calendars/schedule/', {
          params: { start_date: startDate, end_date: endDate },
        });
        commit('setCalendarSummary', computeCalendarSummary(data));
      } catch (error) {
        console.error('Ошибка загрузки сводки календаря:', error);
      }
    },

    // «Календарь изменился» (бронь/запись/отмена — локально или по realtime-
    // уведомлению через invalidationBus 'calendar'): обновляем счётчик дока И
    // поднимаем флаг «новое» (точка на вкладке Календарь до открытия календаря).
    async markCalendarChanged({ commit, dispatch }) {
      await dispatch('fetchCalendarSummary');
      commit('setCalendarHasNew', true);
    },
    // Гасим «новое» — вызывается при открытии CalendarPage.
    clearCalendarNew({ commit }) {
      commit('setCalendarHasNew', false);
    },
  },

  getters: {
    isAuthenticated: (state: State) => !!state.user,
    userId: (state: State) => state.user?.id,
    userRole: (state: State) => state.user?.role || null,
    calendarSummary: (state: State) => state.calendarSummary,
    calendarHasNew: (state: State) => state.calendarHasNew,
    // True when the user is logged in but has not yet provided a phone
    // number — typically OAuth signups before the post-signup phone step.
    // The router uses this to force a redirect to /complete-profile/phone.
    needsPhoneCollection: (state: State) =>
      !!state.user && !(state.user.phone && state.user.phone.trim().length > 0),
    // Issue #10 (P1, partial): exposed for the future OnboardingShell (#5)
    // guard. Returns the user's role declaration that survived the OAuth
    // state round-trip, or null when nothing was declared.
    pendingIntent: (state: State) => state.pendingIntent,
  },

  modules: {
    formActionBar,
  },
});
